// @givmo/app-charity — the fundraiser panel inside the unified statement (CPA-WEB11).
//
// The creator's message (with its required framing and the objection control) and the organizer's
// §317(a) contact, rendered under an expanded fundraiser row in the All and By-fundraiser views.
//
// ★ IT REUSES THE SHIPPED COMPOSITE, IT DOES NOT RE-IMPLEMENT IT. `FundraiserDetailsCard`
// comes straight from `fundraisers-section.tsx` and owns the organizer-first order plus the
// one-card presence matrix. That is deliberate and load-bearing rather than mere thrift: its
// message arm takes `framing` as a REQUIRED non-nullable
// prop, so "a supporter's words never appear unattributed" is enforced by the compiler; the objection
// flow (FCA-BE9) lives inside that panel so it cannot outlive the message it objects to; and the
// framing copy is registry-served, President-signed bytes this repo must never compose. A parallel
// implementation here would be a second place for all three guarantees to drift out of.
//
// ★ NO MONEY COMES FROM THIS READ. The statement is the source for every figure on the screen; this
// route supplies only the flag-gated PANEL content. The fundraiser-detail read and the statement
// legitimately report different `donor_count`/`gross_total` for the same fundraiser (the statement is
// not `domain_type`-filtered, the rollup is), and showing both numbers for one fundraiser on one
// screen is precisely the "three cards that never added up" failure this redesign exists to end.
//
// ★ IT FETCHES ONLY WHILE THE FUNDRAISER IS EXPANDED. The parent row's chevron is the sole disclosure:
// an expanded eligible group mounts this panel above its donations; collapsing the group unmounts
// both. The message is dark in every environment today
// (`FUNDRAISER_CHARITY_MESSAGE_ENABLED` is unset everywhere), so the common outcome is a resolved read
// that renders nothing at all. That is why the panel renders NOTHING rather than a header or a
// placeholder when both halves are absent: an empty framed box under every fundraiser would be worse
// than the feature being invisible until it exists.

import { useQuery } from '@tanstack/react-query';
import { charityAdminGetSection321FundraiserDetailOptions } from '@givmo/api-client/query';
import { Skeleton, testIdProp } from '@givmo/design-system';
import type { ReactNode } from 'react';
import { readMessageFraming } from './grants-decode';
import { FundraiserDetailsCard } from './fundraisers-section';

const PREFIX = 'statement-fundraiser';

export interface StatementFundraiserPanelProps {
  charityId: string;
  fundraiserRef: string;
  /**
   * Owns the semantic table wrapper. Invoked for loading, error, or real content; never for a
   * resolved empty detail, so an absent panel cannot leave a padded phantom row behind.
   */
  render: (panel: ReactNode) => ReactNode;
}

export function StatementFundraiserPanel({
  charityId,
  fundraiserRef,
  render,
}: StatementFundraiserPanelProps) {
  const detail = useQuery(
    charityAdminGetSection321FundraiserDetailOptions({
      path: { charity_id: charityId, fundraiser_id: fundraiserRef },
    }),
  );

  if (detail.isPending) {
    return render(
      <div className="mb-12" {...testIdProp(`${PREFIX}-loading`)}>
        <Skeleton className="h-14 w-1/3" />
      </div>,
    );
  }

  // A failed read no longer vanishes. This panel IS supplementary — the row's money is complete
  // without it — so the failure gets the quietest thing that is still honest: one line and a retry,
  // no icon, no framed error box. Silence was the other defensible reading (a card whose data failed
  // must not disappear, versus navigation's sanctioned omit-on-failure for adjunct items); the
  // deciding argument is that a charity who cannot see a message they know exists has no way to tell
  // "there is nothing here" from "this did not load".
  if (detail.isError) {
    return render(
      <div
        className="mb-12 flex items-center gap-8 font-sans text-s13 tracking-givmo text-text-secondary"
        {...testIdProp(`${PREFIX}-error`)}
      >
        <span>Couldn’t load the organizer’s details.</span>
        <button
          type="button"
          onClick={() => void detail.refetch()}
          className="font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:shadow-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          {...testIdProp(`${PREFIX}-retry`)}
        >
          Try again
        </button>
      </div>,
    );
  }

  const message = detail.data?.message;
  const organizer = detail.data?.organizer;
  // FCA-WEB3: read off the body as `unknown`, fail-closed. `message_framing` is an additive field the
  // committed snapshot does not carry, and a shape this build does not understand withholds the panel
  // rather than rendering a supporter's words with no attribution.
  const framing = readMessageFraming(detail.data);
  const hasMessage = typeof message === 'string' && message.trim().length > 0 && framing !== null;

  if (!hasMessage && !organizer) return null;

  return render(
    <div {...testIdProp(`${PREFIX}-panel`)}>
      <FundraiserDetailsCard
        organizer={organizer}
        message={hasMessage ? message : null}
        framing={framing}
        charityId={charityId}
        fundraiserId={fundraiserRef}
        className="mb-0"
      />
    </div>,
  );
}
